import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  ShieldOff,
  ShieldCheck,
  Trash2,
  Users,
  X,
  Edit as EditIcon,
  Eye,
  Monitor,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { ModalPortal } from '../components/ModalPortal';

/**
 * Page /admin/utilisateurs — US-ADM-02.
 *
 * Fonctionnalités :
 *  - Liste paginée + recherche texte + filtre statut + filtre rôle
 *  - Tri par colonnes (email / nom / dernière connexion / création)
 *  - Création utilisateur (modal)
 *  - Édition (modal — prénom/nom/rôle/service)
 *  - Détail 360° (drawer latéral — identité + sessions actives)
 *  - Actions : activer/désactiver, reset mdp, forcer déconnexion, supprimer
 */

interface UserRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  role_id: string | null;
  service_id: string | null;
  is_active: boolean;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
  must_change_password: boolean;
  additional_roles?: { role_id: string }[];
}

interface UserDetail extends UserRow {
  tenant_id: string | null;
  failed_login_count: number;
  custom_fields: Record<string, unknown>;
  deleted_at: string | null;
  sessions: SessionRow[];
}

interface SessionRow {
  id: string;
  jti: string;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
}

interface RoleOption {
  id: string;
  name: string;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

type SortBy = 'email' | 'first_name' | 'last_name' | 'created_at' | 'last_login_at';
type SortOrder = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'inactive' | 'locked';

const createUserFormSchema = z.object({
  first_name: z.string().min(1, 'Requis').max(100),
  last_name: z.string().min(1, 'Requis').max(100),
  email: z.string().email('Email invalide').max(255),
  role_id: z.string().uuid('Sélectionne un rôle'),
  additional_role_ids: z.array(z.string().uuid()).optional(),
});
type CreateUserFormInput = z.infer<typeof createUserFormSchema>;

const updateUserFormSchema = z.object({
  first_name: z.string().min(1, 'Requis').max(100),
  last_name: z.string().min(1, 'Requis').max(100),
  role_id: z.string().uuid('Sélectionne un rôle'),
  additional_role_ids: z.array(z.string().uuid()).optional(),
});
type UpdateUserFormInput = z.infer<typeof updateUserFormSchema>;

export function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);

  // Debounce de la recherche (300 ms) — évite un appel API par frappe.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page = 1 quand on change un filtre.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, roleFilter, sortBy, sortOrder]);

  useEffect(() => {
    refresh();
  }, [debouncedSearch, statusFilter, roleFilter, sortBy, sortOrder, page]);

  useEffect(() => {
    loadRoles();
  }, []);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (roleFilter) params.set('role_id', roleFilter);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await api.get<{ data: UserRow[]; pagination: Pagination }>(
        `/admin/users?${params}`,
      );
      setUsers(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadRoles() {
    try {
      const res = await api.get<{ data: Array<{ id: string; name: string }> }>('/admin/roles');
      setRoles(res.data.data);
    } catch {
      // L'absence de rôles ne doit pas casser la liste.
    }
  }

  async function handleDeactivate(id: string) {
    try {
      await api.post(`/admin/users/${id}/deactivate`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }
  async function handleActivate(id: string) {
    try {
      await api.post(`/admin/users/${id}/activate`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }
  async function handleResetPassword(user: UserRow) {
    if (!confirm(`Réinitialiser le mot de passe de ${user.email} ?`)) return;
    try {
      const res = await api.post<{ data: { temporary_password?: string } }>(
        `/admin/users/${user.id}/reset-password`,
      );
      if (res.data.data.temporary_password) {
        setResetResult({ email: user.email, password: res.data.data.temporary_password });
      } else {
        alert("Mot de passe réinitialisé — envoyé par email à l'utilisateur.");
      }
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }
  async function handleRevokeSessions(user: UserRow) {
    if (!confirm(`Forcer la déconnexion de ${user.email} ?`)) return;
    try {
      const res = await api.post<{ data: { count: number } }>(
        `/admin/users/${user.id}/revoke-sessions`,
      );
      alert(`${res.data.data.count} session(s) révoquée(s).`);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }
  async function handleDelete(user: UserRow) {
    if (!confirm(`Supprimer définitivement ${user.email} ? (soft delete)`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  function toggleSort(column: SortBy) {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  const rolesById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <Users className="w-7 h-7 mr-2 text-primary-600" />
        Gestion des utilisateurs
      </h1>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-gray-200">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Rechercher (email, prénom, nom)…"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Désactivés</option>
            <option value="locked">Verrouillés</option>
          </select>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les rôles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => refresh()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="ml-auto inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel utilisateur
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh
                  column="email"
                  label="Email"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={toggleSort}
                />
                <SortableTh
                  column="last_name"
                  label="Nom"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={toggleSort}
                />
                <Th className="hidden md:table-cell">Rôle</Th>
                <Th className="hidden lg:table-cell">Statut</Th>
                <SortableTh
                  column="last_login_at"
                  label="Dernière connexion"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={toggleSort}
                  className="hidden xl:table-cell"
                />
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucun utilisateur ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const role = rolesById.get(u.role_id ?? '');
                  const locked = u.locked_until && new Date(u.locked_until) > new Date();
                  return (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td
                        className="px-4 py-3 text-sm text-gray-900 cursor-pointer"
                        onClick={() => setDetailUserId(u.id)}
                      >
                        {u.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {u.first_name} {u.last_name}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
                        {role?.name ?? '—'}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-sm">
                        <StatusBadge active={u.is_active} locked={Boolean(locked)} />
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-xs text-gray-500">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'jamais'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <IconButton
                          title="Détail"
                          onClick={() => setDetailUserId(u.id)}
                          icon={Eye}
                        />
                        <IconButton
                          title="Modifier"
                          onClick={() => setEditingUser(u)}
                          icon={EditIcon}
                        />
                        {u.is_active ? (
                          <IconButton
                            title="Désactiver"
                            onClick={() => handleDeactivate(u.id)}
                            icon={ShieldOff}
                          />
                        ) : (
                          <IconButton
                            title="Réactiver"
                            onClick={() => handleActivate(u.id)}
                            icon={ShieldCheck}
                            tone="success"
                          />
                        )}
                        <IconButton
                          title="Réinitialiser le mot de passe"
                          onClick={() => handleResetPassword(u)}
                          icon={RefreshCw}
                        />
                        <IconButton
                          title="Forcer la déconnexion"
                          onClick={() => handleRevokeSessions(u)}
                          icon={X}
                        />
                        <IconButton
                          title="Supprimer"
                          onClick={() => handleDelete(u)}
                          icon={Trash2}
                          tone="danger"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-600">
              {pagination.total} utilisateur(s) — page {pagination.page} / {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
              </button>
              <button
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <CreateUserModal
          roles={roles}
          onClose={() => setCreateOpen(false)}
          onCreated={(temp) => {
            setCreateOpen(false);
            if (temp) setResetResult(temp);
            refresh();
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            refresh();
          }}
        />
      )}

      {detailUserId && (
        <UserDetailDrawer
          userId={detailUserId}
          roles={rolesById}
          onClose={() => setDetailUserId(null)}
          onAction={() => refresh()}
        />
      )}

      {resetResult && (
        <TemporaryPasswordModal
          email={resetResult.email}
          password={resetResult.password}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tableau — helpers
// ─────────────────────────────────────────────────────────────────────────────

function Th({
  children,
  className,
  align,
}: {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-4 py-3 text-${align ?? 'left'} text-xs font-medium text-gray-500 uppercase ${
        className ?? ''
      }`}
    >
      {children}
    </th>
  );
}

function SortableTh({
  column,
  label,
  sortBy,
  sortOrder,
  onSort,
  className,
}: {
  column: SortBy;
  label: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSort(c: SortBy): void;
  className?: string;
}) {
  const active = sortBy === column;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none ${
        className ?? ''
      }`}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center">
        {label}
        {active &&
          (sortOrder === 'asc' ? (
            <ArrowUp className="w-3 h-3 ml-1" />
          ) : (
            <ArrowDown className="w-3 h-3 ml-1" />
          ))}
      </span>
    </th>
  );
}

function StatusBadge({ active, locked }: { active: boolean; locked: boolean }) {
  if (locked) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Verrouillé
      </span>
    );
  }
  if (active) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Actif
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      Désactivé
    </span>
  );
}

function IconButton({
  title,
  onClick,
  icon: Icon,
  tone = 'default',
}: {
  title: string;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'success' | 'danger';
}) {
  const cls =
    tone === 'danger'
      ? 'text-red-600 hover:bg-red-50'
      : tone === 'success'
        ? 'text-green-600 hover:bg-green-50'
        : 'text-gray-600 hover:bg-gray-100';
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`inline-flex items-center p-2 rounded-lg transition-colors ${cls}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Champ — Rôles additionnels (double rôle, US client)
// ─────────────────────────────────────────────────────────────────────────────

function AdditionalRolesField({
  roles,
  primaryRoleId,
  value,
  onChange,
}: {
  roles: RoleOption[];
  primaryRoleId: string | undefined;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const options = roles.filter((r) => r.id !== primaryRoleId);
  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }
  return (
    <Field label="Rôles additionnels (cumul — optionnel)">
      <div className="flex flex-wrap gap-2">
        {options.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Aucun autre rôle disponible.</p>
        ) : (
          options.map((r) => {
            const checked = value.includes(r.id);
            return (
              <button
                type="button"
                key={r.id}
                onClick={() => toggle(r.id)}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  checked
                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {r.name}
              </button>
            );
          })
        )}
      </div>
    </Field>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal — Création
// ─────────────────────────────────────────────────────────────────────────────

function CreateUserModal({
  roles,
  onClose,
  onCreated,
}: {
  roles: RoleOption[];
  onClose: () => void;
  onCreated: (temp: { email: string; password: string } | null) => void;
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserFormInput>({ resolver: zodResolver(createUserFormSchema) });

  const primaryRoleId = watch('role_id');
  const additionalRoleIds = watch('additional_role_ids') ?? [];

  async function onSubmit(values: CreateUserFormInput) {
    setErrorMessage(null);
    try {
      // Exclut le rôle principal d'éventuels rôles additionnels (cohérence).
      const payload = {
        ...values,
        additional_role_ids: (values.additional_role_ids ?? []).filter(
          (id) => id !== values.role_id,
        ),
      };
      const res = await api.post<{
        data: { user: { email: string }; temporary_password?: string };
      }>('/admin/users', payload);
      onCreated(
        res.data.data.temporary_password
          ? { email: res.data.data.user.email, password: res.data.data.temporary_password }
          : null,
      );
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <ModalShell title="Nouvel utilisateur" onClose={onClose}>
      {errorMessage && <ErrorBanner message={errorMessage} />}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom" error={errors.first_name?.message}>
            <input {...register('first_name')} className={inputClass} />
          </Field>
          <Field label="Nom" error={errors.last_name?.message}>
            <input {...register('last_name')} className={inputClass} />
          </Field>
        </div>
        <Field label="Email" error={errors.email?.message}>
          <input type="email" {...register('email')} className={inputClass} />
        </Field>
        <Field label="Rôle" error={errors.role_id?.message}>
          <select {...register('role_id')} className={inputClass} defaultValue="">
            <option value="" disabled>
              — Choisir un rôle —
            </option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <AdditionalRolesField
          roles={roles}
          primaryRoleId={primaryRoleId}
          value={additionalRoleIds}
          onChange={(next) => setValue('additional_role_ids', next)}
        />
        <FooterActions onClose={onClose} submitting={isSubmitting} submitLabel="Créer" />
      </form>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal — Édition
// ─────────────────────────────────────────────────────────────────────────────

function EditUserModal({
  user,
  roles,
  onClose,
  onSaved,
}: {
  user: UserRow;
  roles: RoleOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UpdateUserFormInput>({
    resolver: zodResolver(updateUserFormSchema),
    defaultValues: {
      first_name: user.first_name,
      last_name: user.last_name,
      role_id: user.role_id ?? '',
      additional_role_ids: (user.additional_roles ?? []).map((r) => r.role_id),
    },
  });

  const primaryRoleId = watch('role_id');
  const additionalRoleIds = watch('additional_role_ids') ?? [];

  async function onSubmit(values: UpdateUserFormInput) {
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        additional_role_ids: (values.additional_role_ids ?? []).filter(
          (id) => id !== values.role_id,
        ),
      };
      await api.patch(`/admin/users/${user.id}`, payload);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <ModalShell title={`Modifier ${user.email}`} onClose={onClose}>
      {errorMessage && <ErrorBanner message={errorMessage} />}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom" error={errors.first_name?.message}>
            <input {...register('first_name')} className={inputClass} />
          </Field>
          <Field label="Nom" error={errors.last_name?.message}>
            <input {...register('last_name')} className={inputClass} />
          </Field>
        </div>
        <Field label="Email (non modifiable)">
          <input value={user.email} disabled className={inputClass + ' bg-gray-50 text-gray-500'} />
        </Field>
        <Field label="Rôle" error={errors.role_id?.message}>
          <select {...register('role_id')} className={inputClass}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <AdditionalRolesField
          roles={roles}
          primaryRoleId={primaryRoleId}
          value={additionalRoleIds}
          onChange={(next) => setValue('additional_role_ids', next)}
        />
        <FooterActions onClose={onClose} submitting={isSubmitting} submitLabel="Enregistrer" />
      </form>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer — Détail 360°
// ─────────────────────────────────────────────────────────────────────────────

function UserDetailDrawer({
  userId,
  roles,
  onClose,
  onAction,
}: {
  userId: string;
  roles: Map<string, RoleOption>;
  onClose: () => void;
  onAction: () => void;
}) {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: UserDetail }>(`/admin/users/${userId}`);
        if (!cancelled) setDetail(res.data.data);
      } catch (err) {
        if (!cancelled) setErrorMessage(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function revokeAll() {
    if (!detail) return;
    if (
      !confirm(`Forcer la déconnexion de ${detail.email} ? Toutes ses sessions seront révoquées.`)
    )
      return;
    try {
      const res = await api.post<{ data: { count: number } }>(
        `/admin/users/${detail.id}/revoke-sessions`,
      );
      const refreshed = await api.get<{ data: UserDetail }>(`/admin/users/${detail.id}`);
      setDetail(refreshed.data.data);
      onAction();
      alert(`${res.data.data.count} session(s) révoquée(s).`);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  /** Révoque une seule session de l'utilisateur cible. */
  async function revokeOne(sessionId: string) {
    if (!detail) return;
    if (!confirm('Révoquer cette session ?')) return;
    try {
      await api.post(`/admin/users/${detail.id}/revoke-sessions`, {
        session_ids: [sessionId],
      });
      const refreshed = await api.get<{ data: UserDetail }>(`/admin/users/${detail.id}`);
      setDetail(refreshed.data.data);
      onAction();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  const role = detail?.role_id ? roles.get(detail.role_id) : null;
  const locked = detail?.locked_until && new Date(detail.locked_until) > new Date();

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Détail utilisateur</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {errorMessage && <ErrorBanner message={errorMessage} />}
          {loading || !detail ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <>
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Identité
                </h3>
                <dl className="text-sm space-y-1.5">
                  <Row label="Nom complet" value={`${detail.first_name} ${detail.last_name}`} />
                  <Row label="Email" value={detail.email} />
                  <Row label="Rôle" value={role?.name ?? '—'} />
                  <Row
                    label="Statut"
                    value={<StatusBadge active={detail.is_active} locked={Boolean(locked)} />}
                  />
                  {locked && detail.locked_until && (
                    <Row
                      label="Verrouillé jusqu'à"
                      value={new Date(detail.locked_until).toLocaleString()}
                    />
                  )}
                  <Row label="Créé le" value={new Date(detail.created_at).toLocaleString()} />
                  <Row
                    label="Dernière connexion"
                    value={
                      detail.last_login_at
                        ? new Date(detail.last_login_at).toLocaleString()
                        : 'jamais'
                    }
                  />
                  <Row label="Échecs login" value={String(detail.failed_login_count)} />
                  <Row
                    label="Doit changer mdp"
                    value={detail.must_change_password ? 'Oui' : 'Non'}
                  />
                </dl>
              </section>

              {Object.keys(detail.custom_fields ?? {}).length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Champs personnalisés
                  </h3>
                  <dl className="text-sm space-y-1.5">
                    {Object.entries(detail.custom_fields).map(([k, v]) => (
                      <Row key={k} label={k} value={String(v ?? '—')} />
                    ))}
                  </dl>
                </section>
              )}

              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center">
                    <Monitor className="w-3.5 h-3.5 mr-1.5" />
                    Sessions actives ({detail.sessions.length})
                  </h3>
                  {detail.sessions.length > 0 && (
                    <button
                      onClick={revokeAll}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Tout révoquer
                    </button>
                  )}
                </div>
                {detail.sessions.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Aucune session active.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.sessions.map((s) => (
                      <div
                        key={s.id}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs flex items-start justify-between gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {s.user_agent ?? 'Agent inconnu'}
                          </p>
                          <p className="text-gray-500 mt-0.5">
                            IP {s.ip ?? '?'} · dernier signe de vie{' '}
                            {new Date(s.last_seen_at).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => revokeOne(s.id)}
                          className="flex-shrink-0 p-1.5 rounded text-red-600 hover:bg-red-50"
                          title="Révoquer cette session"
                          aria-label="Révoquer cette session"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </ModalPortal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500 text-xs">{label}</dt>
      <dd className="text-gray-900 text-right break-all">{value}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal — Mot de passe temporaire
// ─────────────────────────────────────────────────────────────────────────────

function TemporaryPasswordModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <ModalShell title="Mot de passe temporaire" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">
        Mot de passe temporaire généré pour <strong>{email}</strong>. Communique-le par un canal
        sécurisé. L'utilisateur devra le changer à la première connexion.
      </p>
      <p className="text-xs text-gray-400 mb-3">
        🔧 DEV — En production, ce mdp sera envoyé par email (US-ADM-06) et NON exposé ici.
      </p>
      <div className="flex items-center space-x-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <code className="flex-1 text-sm font-mono break-all">{password}</code>
        <button onClick={copy} className="p-1.5 rounded hover:bg-gray-200" aria-label="Copier">
          {copied ? (
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>
      <div className="flex justify-end mt-4">
        <button
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
        >
          Fermer
        </button>
      </div>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UI helpers partagés
// ─────────────────────────────────────────────────────────────────────────────

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </ModalPortal>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

function FooterActions({
  onClose,
  submitting,
  submitLabel,
}: {
  onClose: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div className="flex justify-end space-x-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
      >
        Annuler
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
      >
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {submitLabel}
      </button>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
